import { useMemo } from 'react';
import { Html } from '@react-three/drei';
import { Vector3, Euler, MathUtils } from 'three';

export interface EnvironmentData {
  wind: {
    speed: number; // knots
    direction: number; // degrees (0 = North, 90 = East, etc.)
  };
  current: {
    speed: number; // knots
    direction: number; // degrees (direction current is flowing TO)
  };
  boatHeading: number; // degrees (0 = North, 90 = East, etc.)
}

export interface EnvironmentOptions {
  showWind: boolean;
  showCurrent: boolean;
  showCompass: boolean;
}

interface EnvironmentIndicatorsProps {
  environment: EnvironmentData;
  options: EnvironmentOptions;
  bandRadius?: number; // Radius of the compass band in meters
  bandHeight?: number; // Height of the compass band above water
}

/**
 * EnvironmentIndicators - Shows wind, current, and compass on a rotating band
 * - Compass band rotates based on boat heading
 * - All indicators positioned on the band perimeter at shoulder height
 */
export function EnvironmentIndicators({
  environment,
  options,
  bandRadius = 13.2, // Match water surface radius
  bandHeight = 0.6, // Shoulder height for alignment with watts cards
}: EnvironmentIndicatorsProps) {
  const { wind, current, boatHeading } = environment;
  const { showWind, showCurrent, showCompass } = options;

  // Rotate band to align with boat heading
  // Negative rotation so correct compass direction appears in front of boat
  const bandRotation = useMemo(() => {
    return new Euler(0, -MathUtils.degToRad(boatHeading), 0);
  }, [boatHeading]);

  // Calculate position on band for a given direction
  const getPositionOnBand = (direction: number) => {
    const angleRad = MathUtils.degToRad(direction);
    const x = Math.sin(angleRad) * bandRadius;
    const z = Math.cos(angleRad) * bandRadius;
    return new Vector3(x, bandHeight, z);
  };

  // Convert direction to compass bearing
  const getCompassBearing = (degrees: number): string => {
    const directions = [
      'N',
      'NNE',
      'NE',
      'ENE',
      'E',
      'ESE',
      'SE',
      'SSE',
      'S',
      'SSW',
      'SW',
      'WSW',
      'W',
      'WNW',
      'NW',
      'NNW',
    ];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Get arrow character based on direction
  const getArrowForDirection = (degrees: number): string => {
    const arrows = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
    const index = Math.round(degrees / 45) % 8;
    return arrows[index];
  };

  return (
    <group rotation={bandRotation}>
      {/* Compass Directional Markers - positioned on the band */}
      {showCompass && (
        <>
          <Html position={getPositionOnBand(0)} distanceFactor={15} center sprite>
            <div className="compass-marker compass-marker--north compass-marker--large">N</div>
          </Html>
          <Html position={getPositionOnBand(90)} distanceFactor={15} center sprite>
            <div className="compass-marker compass-marker--east compass-marker--large">E</div>
          </Html>
          <Html position={getPositionOnBand(180)} distanceFactor={15} center sprite>
            <div className="compass-marker compass-marker--south compass-marker--large">S</div>
          </Html>
          <Html position={getPositionOnBand(270)} distanceFactor={15} center sprite>
            <div className="compass-marker compass-marker--west compass-marker--large">W</div>
          </Html>
        </>
      )}

      {/* Wind Indicator - positioned on band where wind comes FROM */}
      {showWind && (
        <Html position={getPositionOnBand(wind.direction)} distanceFactor={15} center sprite>
          <div className="environment-card environment-card--wind">
            <div className="environment-card__header">
              <span className="environment-card__icon">💨</span>
              <span className="environment-card__title">Wind</span>
            </div>
            <div className="environment-card__body">
              <div className="environment-card__value">{wind.speed} kts</div>
              <div className="environment-card__direction">
                {getCompassBearing(wind.direction)} {getArrowForDirection(wind.direction)}
              </div>
            </div>
          </div>
        </Html>
      )}

      {/* Current Indicator - positioned on band in flow direction */}
      {showCurrent && (
        <Html position={getPositionOnBand(current.direction)} distanceFactor={15} center sprite>
          <div className="environment-card environment-card--current">
            <div className="environment-card__header">
              <span className="environment-card__icon">🌊</span>
              <span className="environment-card__title">Current</span>
            </div>
            <div className="environment-card__body">
              <div className="environment-card__value">{current.speed} kts</div>
              <div className="environment-card__direction">
                {getCompassBearing(current.direction)} {getArrowForDirection(current.direction)}
              </div>
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}
